import React from "react";
import { Button, message, Modal, Tag, Tooltip, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import type { Connection } from "@/providers/smarta-documents/documents-api-client";
import { ApiPaths } from "@/providers/smarta-documents/documents-api-client";
import { Label, Tbody, Td, Tr, Value } from "../../components/styles";
import { DISPLAY_DATE_TIME_FORMAT } from "@/v1/constants";
import { AccountingSystem } from "./AccountingSystem";
import { api } from "../client";
import { usePollingCallback } from "@/lib/hooks/usePollingCallback";

const getTexts = (t: TFunction) => ({
  labels: {
    createdAt: t("connectionTable.labels.createdAt"),
    lastBillsSyncedAt: t("connectionTable.labels.lastBillsSyncedAt"),
    lastBillsAddedAt: t("connectionTable.labels.lastBillsAddedAt"),
    lastInvoicesSyncedAt: t("connectionTable.labels.lastInvoicesSyncedAt"),
    lastInvoicesAddedAt: t("connectionTable.labels.lastInvoicesAddedAt"),
    historicalStartDate: t("connectionTable.labels.historicalStartDate"),
    archivedAt: t("connectionTable.labels.archivedAt"),
    erroredAt: t("connectionTable.labels.erroredAt"),
    archived: t("connectionTable.labels.archived"),
    active: t("connectionTable.labels.active"),
    pending: t("connectionTable.labels.pending"),
    errored: t("connectionTable.labels.errored"),
  },
  archiveModal: {
    title: t("connectionTable.archiveModal.title"),
    content: t("connectionTable.archiveModal.content"),
    okText: t("connectionTable.archiveModal.okText"),
    cancelText: t("connectionTable.archiveModal.cancelText"),
  },
  archiveButton: {
    archive: t("connectionTable.archiveButton.archive"),
    archiving: t("connectionTable.archiveButton.archiving"),
  },
  messages: {
    archivalSuccess: t("connectionTable.messages.archivalSuccess"),
    archivalError: t("connectionTable.messages.archivalError"),
  },
});

type Props = {
  data: Connection;
  customerId: string;
};

export const ConnectionTable: React.FC<Props> = ({ data, customerId }) => {
  const { t } = useTranslation("Documents");
  const texts = getTexts(t);
  const queryClient = useQueryClient();

  const { isPolling, startPolling } = usePollingCallback({
    interval: 2000,
    maxAttempts: 15, // 30 seconds max
  });

  const {
    targetSystem,
    historicalStartDate,
    createdAt,
    lastBillsSyncedAt,
    lastBillsAddedAt,
    lastInvoicesSyncedAt,
    lastInvoicesAddedAt,
    archivedAt,
    state,
    erroredAt,
    errorCode,
  } = data;

  const { mutate: archiveConnection, isPending: isArchiving } = api.useMutation(
    "post",
    ApiPaths.postApiConnectionsByCustomerIdByTargetSystemArchive,
    {
      onSuccess: async () => {
        message.success(texts.messages.archivalSuccess);

        startPolling(async () => {
          // Refetch and wait for the result
          const result = await queryClient.fetchQuery({
            queryKey: [
              "get",
              ApiPaths.getApiConnectionsByCustomerId,
              { params: { path: { customerId } } },
            ],
          });

          const queryData = result as { data: Connection[] } | undefined;
          const connection = queryData?.data?.find(
            (c) => c.targetSystem === targetSystem
          );
          return connection?.state === "archived";
        });
      },
      onError: (error) => {
        message.error(error.error?.message || texts.messages.archivalError);
      },
    }
  );

  const handleArchiveClick = () => {
    Modal.confirm({
      title: texts.archiveModal.title,
      icon: <ExclamationCircleOutlined />,
      content: texts.archiveModal.content,
      okText: texts.archiveModal.okText,
      cancelText: texts.archiveModal.cancelText,
      onOk: () => {
        archiveConnection({
          params: {
            path: {
              customerId,
              targetSystem,
            },
          },
        });
      },
    });
  };

  return (
    <Tbody>
      <Tr isHeaderRow hasBorder>
        <Td colSpan={2}>
          <AccountingSystem targetSystem={targetSystem} />
        </Td>
        <Td>
          <Label>{texts.labels.historicalStartDate}</Label>
          <Value>
            {moment(historicalStartDate).format(DISPLAY_DATE_TIME_FORMAT)}
          </Value>
          <Label className="mt-2">{texts.labels.createdAt}</Label>
          <Value>{moment(createdAt).format(DISPLAY_DATE_TIME_FORMAT)}</Value>
        </Td>
        <Td>
          <Label>{texts.labels.lastBillsSyncedAt}</Label>
          <Value>
            {lastBillsSyncedAt
              ? moment(lastBillsSyncedAt).format(DISPLAY_DATE_TIME_FORMAT)
              : "-"}
          </Value>
          <Label className="mt-2">{texts.labels.lastBillsAddedAt}</Label>
          <Value>
            {lastBillsAddedAt
              ? moment(lastBillsAddedAt).format(DISPLAY_DATE_TIME_FORMAT)
              : "-"}
          </Value>
        </Td>
        <Td>
          <Label>{texts.labels.lastInvoicesSyncedAt}</Label>
          <Value>
            {lastInvoicesSyncedAt
              ? moment(lastInvoicesSyncedAt).format(DISPLAY_DATE_TIME_FORMAT)
              : "-"}
          </Value>
          <Label className="mt-2">{texts.labels.lastInvoicesAddedAt}</Label>
          <Value>
            {lastInvoicesAddedAt
              ? moment(lastInvoicesAddedAt).format(DISPLAY_DATE_TIME_FORMAT)
              : "-"}
          </Value>
        </Td>
        <Td>
          {erroredAt && (
            <>
              <Label>{texts.labels.erroredAt}</Label>
              <Value>
                {moment(erroredAt).format(DISPLAY_DATE_TIME_FORMAT)}
              </Value>
            </>
          )}
          {archivedAt && (
            <>
              <Label className={erroredAt ? "mt-2" : undefined}>
                {texts.labels.archivedAt}
              </Label>
              <Value>
                {moment(archivedAt).format(DISPLAY_DATE_TIME_FORMAT)}
              </Value>
            </>
          )}
        </Td>
        <Td className="text-center">
          {state === "archived" ? (
            <Tag>
              <Typography.Text type="secondary">
                {texts.labels.archived}
              </Typography.Text>
            </Tag>
          ) : state === "pending" ? (
            <Tag color="processing">{texts.labels.pending}</Tag>
          ) : state === "errored" ? (
            <Tooltip title={errorCode}>
              <Tag color="error">{texts.labels.errored}</Tag>
            </Tooltip>
          ) : (
            <Tag color="success">{texts.labels.active}</Tag>
          )}
        </Td>
        <Td className="text-right">
          <Button
            type="link"
            size="small"
            onClick={handleArchiveClick}
            loading={isArchiving || isPolling}
            disabled={state === "archived" || isArchiving || isPolling}
          >
            {isArchiving || isPolling
              ? texts.archiveButton.archiving
              : texts.archiveButton.archive}
          </Button>
        </Td>
      </Tr>
    </Tbody>
  );
};
